"use client";

/**
 * @file Index Workerサイドバーのクライアントサイドソートコンポーネント
 * クライアントサイドのソート機能のみを処理
 */

import { Trans } from "@lingui/react/macro";
import { type FragmentOf, readFragment } from "gql.tada";
import { ChartLineIcon, SettingsIcon } from "lucide-react";
import React, { useMemo } from "react";
import { IW_POLICIES } from "@/lib/authorization/policies/index-worker";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import { useViewOrder } from "../../../_contexts/view-order-context";
import { updateItemOrder } from "../../../iw/[projectId]/(top)/actions";
import { useUpdateViewOrder } from "../../sortable/sortable-hooks";
import SortableSidebarGroup from "../../sortable/sortable-sidebar-group";
import {
  type IndexWorkerItem,
  SPECIAL_VIEW_IDS,
} from "../../sortable/sortable-types";
import { reorderViewsByIds } from "../../sortable/sortable-utils";
import { IndexWorkerProjectItemFragment } from "./fragments";

interface IWSidebarItem extends IndexWorkerItem {
  sortable?: boolean;
  isSettingsItem?: boolean;
}

interface SortableProjectViewsProps {
  project: FragmentOf<typeof IndexWorkerProjectItemFragment>;
  workspaceId: string;
}

export function IwSidebarSortable({
  project: projectFragment,
  workspaceId,
}: SortableProjectViewsProps) {
  const project = readFragment(IndexWorkerProjectItemFragment, projectFragment);

  /** Contextを使用（IDの順序のみを管理） */
  const { getViewOrder } = useViewOrder();
  const checkPolicy = useCheckPolicy();
  const canEditViews = checkPolicy(IW_POLICIES["iw-job:edit"]).ok;
  const canEditProject = checkPolicy(IW_POLICIES["iw-project:edit"]).ok;

  // 共通フックで楽観的更新とエラーハンドリングを処理
  const handleUpdateViewOrder = useUpdateViewOrder(
    (actionId: string, viewIds: string[], projectId: string) =>
      updateItemOrder(viewIds, projectId),
  );

  // ストアからIDの並び順を取得
  const storedOrderIds = getViewOrder(project.id);

  // ストアに保存されているID順序があればそれを使用してビューを並び替え
  const orderedViews = useMemo(
    () => reorderViewsByIds(project.indexWorkers || [], storedOrderIds),
    [storedOrderIds, project.indexWorkers],
  );

  const views: IWSidebarItem[] = useMemo(() => {
    const result: IWSidebarItem[] = [];

    // 通常のビューを追加（ソート可能） - 順序を保持
    result.push(
      ...orderedViews.map((indexWorker) => ({
        id: indexWorker.id,
        name: indexWorker.name,
        workspaceId,
        projectId: project.id,
        projectType: "iw" as const,
        sortable: canEditViews,
      })),
    );

    // "Settings"ビューを追加（ソート不可、常に最後）: プロジェクト編集権限がある場合のみ
    if (canEditProject) {
      result.push({
        id: SPECIAL_VIEW_IDS.SETTINGS,
        name: "Settings",
        workspaceId,
        projectId: project.id,
        projectType: "iw",
        sortable: false,
        isSettingsItem: true,
      });
    }

    return result;
  }, [canEditViews, canEditProject, orderedViews, workspaceId, project.id]);

  // ビューアイテムのhrefを生成
  const generateViewHref = (item: IWSidebarItem): string => {
    if (item.isSettingsItem) {
      return `/${encodeURIComponent(
        workspaceId,
      )}/iw/${encodeURIComponent(project.id)}/settings`;
    }

    return `/${encodeURIComponent(
      workspaceId,
    )}/iw/${encodeURIComponent(project.id)}/${encodeURIComponent(item.id)}`;
  };

  // ビューコンテンツをレンダリング
  const renderViewContent = (item: IWSidebarItem): React.ReactNode => {
    if (item.isSettingsItem) {
      return (
        <>
          <SettingsIcon className="w-4 h-4" />
          <Trans>Settings</Trans>
        </>
      );
    }

    return (
      <>
        <ChartLineIcon className="w-4 h-4 shrink-0" />
        <div className="line-clamp-2 min-w-0" title={item.name}>
          {item.name}
        </div>
      </>
    );
  };

  return (
    <SortableSidebarGroup
      views={views}
      updateViewOrder={handleUpdateViewOrder}
      projectId={project.id}
      projectType="iw"
      actionId={project.id}
      className="h-auto min-h-7"
      generateViewHref={generateViewHref}
      renderViewContent={renderViewContent}
      isReorderEnabled={canEditViews}
    />
  );
}
